import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Shield, Zap, Package, ArrowRight, Wind, Calendar, Clock, FileText, CheckCircle, MapPin, Users, Mail } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { products, compatibilityNote, liftCompatibilityStatement } from "../data/products";
import { useCart } from "../context/CartContext";
import { useCommerce } from "../context/CommerceContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// REAL FIELD PHOTOGRAPHY - Highwall Lift in actual use
const fieldImages = {
  // NEW field stills - best quality shots showing complete setup
  liftAndTower: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/9h3z5yos_06_lift-and-tower_square.jpg.jpeg",
  againstBuilding: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/0o94ix6q_07_against-building_square.jpg.jpeg",
  finalPlacement: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/cs2x21mp_08_final-placement_square.jpg.jpeg",
  // Original field stills
  fullBoomWithPanel: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/mqrx914x_HWL_field_still_03_048s.jpg",
  boomLiftElevated: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/3ack3jic_HWL_field_still_04_060s.jpg",
};

// REAL FIELD VIDEO - Highwall Lift demonstration (H.264 MP4 for cross-browser support)
const fieldVideo = "/videos/field_demo.mp4";

// Product images - original studio/product shots
const productImages = {
  // Scissor Lift Hangers (for Drywall/Sheetrock)
  scissorLiftMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/hnmxvm2w_Highwall%20Scissor%20Lift%20Hanger%20on%20Skyjack3.jpg",
  scissorLiftPair: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/2hzy6y9m_Highwall%20Scissor%20Lift%20Hanger%20Pair.jpg",
  // Boom Lift Hangers (for IMP - with white UHMW pads)
  boomLiftMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/ujlxoadu_Boom%20Lift%20Hang.jpg",
  boomLiftHangers1: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/je2x8ku1_hanger3.jpg",
  boomLiftHangers2: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/htgmy3k7_Hanger4.jpg",
};

// Hero background - Original Pexels construction scene
const heroBackground = "https://images.pexels.com/photos/13532460/pexels-photo-13532460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// US States for dropdown
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const Hero = () => (
  <section 
    className="min-h-[90vh] relative flex items-center"
    style={{
      backgroundImage: `url(${heroBackground})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}
    data-testid="hero-section"
  >
    <div className="absolute inset-0 bg-black/75" />
    
    {/* Marquee background text */}
    <div className="absolute inset-0 overflow-hidden flex items-center opacity-[0.04] pointer-events-none">
      <div className="whitespace-nowrap text-[200px] font-heading font-black uppercase text-white animate-marquee">
        HIGHWALL™ LIFT-HANGER /// THE ULTIMATE HIGH-WALL SOLUTION /// HIGHWALL™ LIFT-HANGER /// THE ULTIMATE HIGH-WALL SOLUTION ///
      </div>
    </div>
    
    <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 py-32">
      <div className="max-w-4xl">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-6" data-testid="hero-tagline">
          Precision Panel Installation at Height
        </p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white leading-[0.9] mb-8">
          Highwall™<br />
          <span className="text-signal-red">Lift-Hanger</span>
        </h1>
        <p className="text-xl text-gray-300 max-w-2xl mb-10 leading-relaxed">
          From work inside the four walls to the building envelope outside them, Highwall Lift helps crews 
          position large-format construction materials at height. Buddy™ is built for interior commercial 
          construction applications, while Hank™ is built for IMP, glass, and building-envelope work—keeping 
          your deck clear and improving placement accuracy.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a 
            href="#quote" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-signal-red text-white font-bold uppercase tracking-wide hover:bg-signal-red/90 hover:shadow-[0_0_30px_rgba(255,51,51,0.4)] transition-all duration-300"
            data-testid="hero-cta-quote"
          >
            <FileText className="w-5 h-5" />
            Request a Quote
          </a>
          <a 
            href="tel:8067293701" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold uppercase tracking-wide border border-white/20 hover:bg-white/20 transition-all duration-300"
            data-testid="hero-cta-call"
          >
            <Phone className="w-5 h-5" />
            (806) 729-3701
          </a>
        </div>
      </div>
    </div>
  </section>
);

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { commerceEnabled } = useCommerce();
  return (
  <div className="bg-[#1A1A1A] border border-white/10 overflow-hidden group hover:-translate-y-2 transition-all duration-300" data-testid={`product-${product.id}`}>
    <Link to={product.to} className="block aspect-[4/3] overflow-hidden">
      <img 
        src={product.image}
        alt={product.imageAlt}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
    </Link>
    <div className="p-8">
      <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-2">
        Highwall Lift
      </p>
      <Link to={product.to} data-testid={`product-${product.id}-link`}>
        <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white mb-1 hover:text-signal-red transition-colors" data-testid={`product-${product.id}-name`}>
          {product.name}
        </h3>
      </Link>
      <p className="font-mono text-gray-500 uppercase tracking-[0.2em] text-xs mb-2" data-testid={`product-${product.id}-model`}>
        {product.model}
      </p>
      <p className="font-heading text-lg font-semibold text-gray-200 mb-1" data-testid={`product-${product.id}-descriptor`}>
        {product.descriptor}
      </p>
      <p className="text-signal-red italic mb-4" data-testid={`product-${product.id}-tagline`}>
        {product.tagline}
      </p>
      <p className="text-gray-400 mb-4 leading-relaxed">{product.blurb}</p>
      <div className="mb-4">
        <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Primary Applications</p>
        <ul className="text-gray-400 space-y-1 text-sm">
          {product.applications.map((app) => <li key={app}>• {app}</li>)}
        </ul>
      </div>
      <p className="text-gray-400 text-sm mb-6" data-testid={`product-${product.id}-lift-compatibility`}>
        {liftCompatibilityStatement}
      </p>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-gray-500 text-sm uppercase tracking-wide">Price</p>
          <p className="font-heading text-3xl font-black text-white" data-testid={`product-${product.id}-price`}>
            {product.price}<span className="text-lg text-gray-400">{product.priceUnit}</span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {commerceEnabled && (
          <button
            type="button"
            onClick={() => addItem(product.sku, 1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm tracking-wide hover:bg-signal-red/90 transition-all duration-300"
            data-testid={`add-to-cart-${product.id}`}
          >
            Add to Cart
            <ArrowRight className="w-4 h-4" />
          </button>
          )}
          <a href="#quote" className={commerceEnabled ? "text-gray-300 text-sm font-bold uppercase tracking-wide hover:text-white transition-colors" : "inline-flex items-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm tracking-wide hover:bg-signal-red/90 transition-all duration-300"} data-testid={`quote-${product.id}`}>
            Request Quote{!commerceEnabled && <ArrowRight className="w-4 h-4" />}
          </a>
          <Link to={product.to} className="text-signal-red text-sm font-bold uppercase tracking-wide hover:text-white transition-colors" data-testid={`product-${product.id}-details`}>
            View {product.name} Details
          </Link>
        </div>
      </div>
    </div>
  </div>
);
};

const Products = () => (
  <section id="products" className="py-24 sm:py-32 bg-[#0A0A0A]" data-testid="products-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <div className="text-center mb-16">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
          Our Products
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white">
          Choose Your Product
        </h2>
        <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
          Two products, sold by the set. Both work with scissor lifts and boom lifts, subject to applicable lift compatibility and Highwall instructions.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {products.map((product) => <ProductCard key={product.id} product={product} />)}
      </div>

      <p className="text-gray-500 text-sm mt-10 max-w-3xl mx-auto text-center" data-testid="compatibility-note">
        {compatibilityNote}
      </p>
    </div>
  </section>
);


const Features = () => (
  <section id="features" className="py-24 sm:py-32 bg-[#141414] relative" data-testid="features-section">
    <div 
      className="absolute inset-0 opacity-10"
      style={{
        backgroundImage: `url(https://images.pexels.com/photos/6474313/pexels-photo-6474313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
    <div className="absolute inset-0 bg-[#141414]/95" />
    
    <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8">
      <div className="text-center mb-16">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
          Why Choose Highwall™
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white">
          Built for Pros
        </h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Large feature */}
        <div className="md:col-span-7 bg-[#1A1A1A] border border-white/10 p-10 relative overflow-hidden group" data-testid="feature-deck-space">
          <div className="relative z-10">
            <div className="w-16 h-16 bg-signal-red/10 flex items-center justify-center mb-6">
              <Package className="w-8 h-8 text-signal-red" />
            </div>
            <h3 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Save Deck Space
            </h3>
            <p className="text-gray-400 text-lg leading-relaxed">
              Unlike bulky carts, our hangers mount externally to the lift cage, keeping your 
              workspace completely clear and safe. No more juggling materials inside the basket.
            </p>
          </div>
          <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-signal-red/5 rounded-full blur-3xl group-hover:bg-signal-red/10 transition-all duration-500" />
        </div>
        
        {/* Smaller features */}
        <div className="md:col-span-5 bg-[#1A1A1A] border border-white/10 p-10" data-testid="feature-capacity">
          <div className="w-16 h-16 bg-signal-red/10 flex items-center justify-center mb-6">
            <Shield className="w-8 h-8 text-signal-red" />
          </div>
          <h3 className="font-heading text-2xl font-bold uppercase text-white mb-4">
            106 lb Capacity
          </h3>
          <p className="text-gray-400 leading-relaxed">
            Safely supports single panels including drywall, insulated metal panels, and architectural glass. Built tough for demanding jobsite conditions.
          </p>
        </div>
        
        <div className="md:col-span-5 bg-[#1A1A1A] border border-white/10 p-10" data-testid="feature-versatile">
          <div className="w-16 h-16 bg-signal-red/10 flex items-center justify-center mb-6">
            <Zap className="w-8 h-8 text-signal-red" />
          </div>
          <h3 className="font-heading text-2xl font-bold uppercase text-white mb-4">
            Multiple Applications
          </h3>
          <p className="text-gray-400 leading-relaxed">
            Buddy™ for interior commercial construction—drywall, sheetrock, and interior panels. Hank™ for IMPs, architectural glass, curtain wall glazing, and building-envelope work. Both work with scissor lifts and boom lifts, subject to applicable lift compatibility and Highwall instructions.
          </p>
        </div>
        
        <div className="md:col-span-7 bg-signal-red p-10 relative overflow-hidden" data-testid="feature-ships-fast">
          <div className="relative z-10">
            <h3 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              We ship pronto. Request a quote today and get your equipment fast — because jobsite timelines don&apos;t wait.
            </p>
            <a 
              href="#quote"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold uppercase text-sm tracking-wide hover:bg-white/90 transition-all duration-300"
              data-testid="feature-quote-cta"
            >
              <FileText className="w-4 h-4" />
              Request a Quote
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Video Demonstration Section
const VideoDemo = () => (
  <section id="video" className="py-24 sm:py-32 bg-black" data-testid="video-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <div className="text-center mb-12">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
          Real Field Use
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white">
          See Highwall™ Lift in Action
        </h2>
        <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
          This is actual field footage demonstrating the Highwall™ Lift-Hanger being used with aerial equipment on a real construction site—not a rendering or concept video.
        </p>
      </div>
      
      <div className="max-w-5xl mx-auto">
        <div className="relative aspect-video bg-[#1A1A1A] border border-white/10 overflow-hidden">
          <video
            controls
            playsInline
            preload="metadata"
            poster={fieldImages.fullBoomWithPanel}
            className="w-full h-full object-cover"
            data-testid="field-demo-video"
          >
            <source src={fieldVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="p-6 bg-[#1A1A1A] border border-white/10">
            <div className="w-12 h-12 bg-signal-red/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-signal-red" />
            </div>
            <h3 className="font-heading text-lg font-bold text-white mb-2">Real Equipment</h3>
            <p className="text-gray-400 text-sm">Actual boom lift with Highwall™ Lift-Hanger on a commercial jobsite</p>
          </div>
          <div className="p-6 bg-[#1A1A1A] border border-white/10">
            <div className="w-12 h-12 bg-signal-red/10 flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-signal-red" />
            </div>
            <h3 className="font-heading text-lg font-bold text-white mb-2">Real Crew</h3>
            <p className="text-gray-400 text-sm">Professional installers demonstrating panel positioning at height</p>
          </div>
          <div className="p-6 bg-[#1A1A1A] border border-white/10">
            <div className="w-12 h-12 bg-signal-red/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-signal-red" />
            </div>
            <h3 className="font-heading text-lg font-bold text-white mb-2">Real Results</h3>
            <p className="text-gray-400 text-sm">Clear deck space, improved safety, and precision installation</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Gallery = () => (
  <section id="gallery" className="py-24 sm:py-32 bg-[#0A0A0A]" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <div className="text-center mb-16">
        <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
          Real Field Use
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white">
          See It Work
        </h2>
        <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
          Actual Highwall™ Lift-Hanger in use on commercial construction sites
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* NEW PHOTO: Lift and Tower - BEST shot showing crew, boom lift, and panel */}
        <div className="relative overflow-hidden group" data-testid="gallery-field-1">
          <img 
            src={fieldImages.liftAndTower}
            alt="Highwall Lift-Hanger in use - two crew members positioning panel from boom lift basket"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
            <p className="text-signal-red font-mono text-xs uppercase tracking-wider mb-1">Panel Installation</p>
            <p className="text-white font-heading font-bold text-lg">Crew Positioning Panel at Height</p>
          </div>
        </div>
        
        {/* NEW PHOTO: Against Building - shows building context */}
        <div className="relative overflow-hidden group" data-testid="gallery-field-2">
          <img 
            src={fieldImages.againstBuilding}
            alt="Highwall Lift-Hanger - boom lift positioned at building exterior for panel work"
            loading="lazy"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
            <p className="text-signal-red font-mono text-xs uppercase tracking-wider mb-1">Building Application</p>
            <p className="text-white font-heading font-bold text-lg">Exterior Panel Work</p>
          </div>
        </div>
        
        {/* NEW PHOTO: Final Placement - shows completion workflow */}
        <div className="relative overflow-hidden group" data-testid="gallery-field-3">
          <img 
            src={fieldImages.finalPlacement}
            alt="Highwall Lift-Hanger - boom lift operator completing panel placement"
            loading="lazy"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
            <p className="text-signal-red font-mono text-xs uppercase tracking-wider mb-1">Installation Workflow</p>
            <p className="text-white font-heading font-bold text-lg">Precision Placement</p>
          </div>
        </div>
        
        {/* PRODUCT DETAIL: Boom Lift Hanger with UHMW pads */}
        <div className="relative overflow-hidden group" data-testid="gallery-product-detail">
          <img 
            src={productImages.boomLiftHangers1}
            alt="Highwall Lift Hank (Model B) detail - UHMW pads protect panel surfaces"
            loading="lazy"
            className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
            <p className="text-signal-red font-mono text-xs uppercase tracking-wider mb-1">Product Detail</p>
            <p className="text-white font-heading font-bold text-lg">UHMW Panel Protection</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Blog preview on homepage - links to /blog
const BlogPreview = () => {
  const blogPosts = [
    {
      id: 4,
      slug: "boosting-rental-roi-texas-market",
      title: "Boosting Rental ROI: How Specialized Lift Attachments Differentiate Your Fleet in the Texas Market",
      date: "March 31, 2026",
      readTime: "9 min read",
      excerpt: "The Texas rental market is a different beast. From data center builds in North Fort Worth to industrial warehouses in Houston, the demand for aerial work platforms is relentless. But the struggle is real: commodity pricing.",
      image: fieldImages.boomLiftElevated,
    },
    {
      id: 3,
      slug: "best-drywall-hanging-tools-commercial-jobs",
      title: "How to Choose the Best Drywall Hanging Tools for High-Volume Commercial Jobs",
      date: "March 30, 2026",
      readTime: "10 min read",
      excerpt: "High-volume commercial drywall jobs are a different beast entirely. We aren't talking about a weekend basement renovation. We're talking about thousands of sheets of 5/8\" Type X.",
      image: productImages.scissorLiftPair,
    },
  ];

  return (
    <section id="blog" className="py-24 sm:py-32 bg-[#141414]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-16 gap-6">
          <div>
            <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
              Industry Insights
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white">
              Blog
            </h2>
          </div>
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-signal-red font-bold uppercase text-sm tracking-wide hover:gap-4 transition-all"
            data-testid="view-all-blog"
          >
            View All Articles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {blogPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-[#1A1A1A] border border-white/10 overflow-hidden group"
              data-testid={`blog-post-${post.id}`}
            >
              <Link to={`/blog/${post.slug}`}>
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4 leading-tight group-hover:text-signal-red transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Request Form Component
const QuoteRequest = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    product: '',
    quantity: '',
    liftType: '',
    comments: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  // Cart → quote handoff (large/freight/international/dealer orders). Informational only; no pricing is trusted from the browser.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('hwl_quote_prefill');
      if (!raw) return;
      const p = JSON.parse(raw);
      sessionStorage.removeItem('hwl_quote_prefill');
      const skus = new Set((p.lines || []).map((l) => l.sku));
      const product = skus.size > 1 ? 'Both Products' : skus.has('HWL-HANK') ? 'The Hank - Model B ($397/set)' : skus.has('HWL-BUDDY') ? 'The Buddy - Model A ($299/set)' : '';
      const quantity = (p.lines || []).map((l) => `${l.name} (${l.model}) × ${l.quantity} set${l.quantity === 1 ? '' : 's'}`).join('; ');
      const reasons = { international: 'International order — please quote freight, duties and taxes.', freight_quote: 'Multi-set order — please quote freight.', unavailable: 'Please quote freight for this order.', cart: 'Quote requested from cart.' };
      const c = p.customer || {};
      setFormData((f) => ({ ...f, product, quantity, liftType: c.lift_type || f.liftType,
        firstName: c.first_name || f.firstName, lastName: c.last_name || f.lastName, company: c.company || f.company, email: c.email || f.email, phone: c.phone || f.phone,
        city: c.city || f.city, state: c.state || f.state,
        comments: [reasons[p.reason] || '', p.country && p.country !== 'US' ? `Destination country: ${p.country}` : ''].filter(Boolean).join(' ') }));
      setPrefilled(true);
      setTimeout(() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { /* ignore */ }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit quote request. Please try again or call us directly.');
      }
    } catch (err) {
      setError('Failed to submit quote request. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="quote" className="py-24 sm:py-32 bg-[#0A0A0A]" data-testid="quote-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <div className="bg-[#1A1A1A] border border-white/10 p-12 text-center" data-testid="quote-success">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white mb-6">
              Thank You!
            </h2>
            <p className="text-xl text-gray-300 mb-4">
              Your request has been received.
            </p>
            <p className="text-gray-400 mb-8">
              A Highwall™ representative will contact you as soon as possible to discuss your project and provide pricing.
            </p>
            <div className="bg-[#0A0A0A] border border-white/10 p-6 inline-block">
              <p className="text-gray-400 mb-2">If your request is urgent, please call:</p>
              <a href="tel:8067293701" className="flex items-center justify-center gap-2 text-2xl font-bold text-signal-red hover:text-white transition-colors">
                <Phone className="w-6 h-6" />
                (806) 729-3701
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="py-24 sm:py-32 bg-[#0A0A0A]" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-8">
        <div className="text-center mb-12">
          <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
            Get Pricing
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-6">
            Request a Quote
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Complete the form below and a Highwall™ representative will contact you with pricing and ordering information.
          </p>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 p-8 sm:p-12">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 mb-6" data-testid="quote-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="quote-form">
              {prefilled && <p className="md:col-span-2 p-3 border border-signal-red/40 bg-signal-red/10 text-gray-200 text-sm" data-testid="quote-prefilled-notice">Your cart details have been added below. Review and submit — a Highwall representative will follow up with pricing and freight.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="firstName">
                  First Name <span className="text-signal-red">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="John"
                  data-testid="input-firstName"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="lastName">
                  Last Name <span className="text-signal-red">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="Smith"
                  data-testid="input-lastName"
                />
              </div>

              {/* Company */}
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2" htmlFor="company">
                  Company Name <span className="text-signal-red">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="ABC Construction Inc."
                  data-testid="input-company"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="email">
                  Email <span className="text-signal-red">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="john@company.com"
                  data-testid="input-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="phone">
                  Phone <span className="text-signal-red">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="(555) 123-4567"
                  data-testid="input-phone"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="city">
                  City <span className="text-signal-red">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="Dallas"
                  data-testid="input-city"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="state">
                  State <span className="text-signal-red">*</span>
                </label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  data-testid="input-state"
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Product */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="product">
                  Product Interested In <span className="text-signal-red">*</span>
                </label>
                <select
                  id="product"
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  data-testid="input-product"
                >
                  <option value="">Select Product</option>
                  <option value="The Buddy - Model A ($299/set)">The Buddy™ — Model A — $299/set</option>
                  <option value="The Hank - Model B ($397/set)">The Hank™ — Model B — $397/set</option>
                  <option value="Both Products">Both Products</option>
                  <option value="Not Sure / Help Me Choose">Not Sure / Help Me Choose</option>
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-gray-400 text-sm mb-2" htmlFor="quantity">
                  Estimated Quantity Needed
                </label>
                <input
                  type="text"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  placeholder="e.g., 5 sets"
                  data-testid="input-quantity"
                />
              </div>

              {/* Lift type (informational only) */}
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2" htmlFor="liftType">
                  What type of lift are you currently using? <span className="text-gray-600">(optional)</span>
                </label>
                <select
                  id="liftType"
                  name="liftType"
                  value={formData.liftType}
                  onChange={handleChange}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                  data-testid="input-liftType"
                >
                  <option value="">Select (optional)</option>
                  <option value="Scissor Lift">Scissor Lift</option>
                  <option value="Boom Lift">Boom Lift</option>
                  <option value="Both">Both</option>
                  <option value="Not Sure">Not Sure</option>
                </select>
              </div>

              {/* Comments */}
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2" htmlFor="comments">
                  Project Description / Comments
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all resize-none"
                  placeholder="Tell us about your project, timeline, or any specific requirements..."
                  data-testid="input-comments"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-8 bg-signal-red text-white font-bold uppercase tracking-wide py-4 hover:bg-signal-red/90 hover:shadow-[0_0_30px_rgba(255,51,51,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-quote"
            >
              {loading ? 'Submitting...' : (
                <>
                  <FileText className="w-5 h-5" />
                  Request Quote
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Failed to send message. Please try again or call us directly.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#141414]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-8">
              Contact Us
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-10">
              Have questions about our products or need help choosing the right solution? Our team is ready to assist you.
            </p>
            
            <div className="space-y-6">
              <a 
                href="tel:8067293701" 
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-signal-red flex items-center justify-center group-hover:bg-signal-red/80 transition-colors">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide">Phone</p>
                  <p className="text-white text-xl font-bold">(806) 729-3701</p>
                </div>
              </a>
              
              <a 
                href="mailto:sales@highwalllift.com" 
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide">Email</p>
                  <p className="text-white text-xl font-bold">sales@highwalllift.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide">Business Hours</p>
                  <p className="text-white font-bold">Mon - Fri: 8:00 AM - 5:00 PM CST</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide">Service Area</p>
                  <p className="text-white font-bold" data-testid="service-area">U.S. &amp; International Shipping</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1A1A1A] border border-white/10 p-8 sm:p-10">
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-4">Message Sent!</h3>
                <p className="text-gray-400">We&apos;ll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="font-heading text-2xl font-bold uppercase text-white mb-6">
                  Send a Message
                </h3>
                
                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 mb-4" data-testid="contact-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2" htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                      placeholder="Your name"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2" htmlFor="contactEmail">Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                      placeholder="your@email.com"
                      required
                      data-testid="input-contactEmail"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2" htmlFor="contactPhone">Phone</label>
                    <input
                      type="tel"
                      id="contactPhone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all"
                      placeholder="(555) 123-4567"
                      data-testid="input-contactPhone"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2" htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white placeholder-gray-600 focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all resize-none"
                      placeholder="How can we help you?"
                      required
                      data-testid="input-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-signal-red text-white font-bold uppercase tracking-wide py-4 hover:bg-signal-red/90 hover:shadow-[0_0_30px_rgba(255,51,51,0.4)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-contact"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <>
      <SEOHead
        title="Highwall Lift - Precision Panel Installation Equipment"
        description="Transform your boom or scissor lift into a precision installation tool. The Highwall Lift-Hanger securely positions drywall, insulated metal panels, and architectural glass at height."
        keywords="highwall lift, panel installation, drywall hanger, boom lift attachment, scissor lift hanger, IMP installation, construction equipment"
        canonicalPath=""
      />
      
      <main>
        <Hero />
        <Products />
        <Features />
        <VideoDemo />
        <Gallery />
        <BlogPreview />
        <QuoteRequest />
        <Contact />
      </main>
    </>
  );
};

export default HomePage;
